import React, { useRef, useImperativeHandle, forwardRef } from "react";
import { MButton } from "../atoms/MButtons";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { IconUpload, IconTrash, IconFile } from "@tabler/icons-react";
import { useMedia } from "../../../hooks/useMedia";

import { useDocument } from "../../../hooks/useDocument";
import type { CreateDocumentDto } from "../../../types/document.dto";
import { FileCategory, DocumentStatus } from "../../../types/document.dto";
import { triggerNativeCameraCapture } from "@/utils/nativeBridge";
import { handlePickerResultMessage } from "@/utils/document-utils";

export interface MDocUploadHandle {
  handleDone: () => Promise<void>;
  mediaFiles: any[];
  uploading: boolean;
}

interface MDocUploadProps {
  onFilesChange?: (files: any[]) => void;
}

export const MDocUpload = forwardRef<MDocUploadHandle, MDocUploadProps>(
  ({ onFilesChange }, ref) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { upload, uploading, mediaFiles, remove, setMediaFiles } = useMedia(); // remove now expects only fileKey
    const { showToast } = useToast();

    // Get memberId from localStorage (legacy pattern)
    let memberId: number | string | undefined = undefined;
    try {
      let memberRaw = localStorage.getItem("selectedMember");
      if (!memberRaw) {
        memberRaw = localStorage.getItem("member");
      }
      if (memberRaw) {
        const memberObj = JSON.parse(memberRaw);
        memberId = memberObj?.id ? String(memberObj.id) : undefined;
      }
    } catch (e) {
      memberId = undefined;
    }

    const { createDocument } = useDocument(memberId as string);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      try {
        for (let i = 0; i < files.length; i++) {
          if (!memberId) {
            showToast({
              message: "Member ID is missing, cannot upload.",
              variant: "error",
            });
            return;
          }
          await upload(files[i], memberId as string);
        }
        showToast({ message: "Upload successful", variant: "success" });
      } catch (err) {
        showToast({ message: "Upload failed", variant: "error" });
      }
      if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleRemove = async (fileKey: string) => {
      try {
        await remove(fileKey);
        showToast({ message: "File deleted", variant: "success" });
      } catch (err) {
        showToast({ message: "Delete failed", variant: "error" });
      }
    };

    // Listen for picker_result from native app
    React.useEffect(() => {
      async function handleNativeMessage(event: MessageEvent) {
        try {
          const files = await handlePickerResultMessage(event);
          for (const file of files) {
            if (memberId) {
              await upload(file, memberId as string);
            }
          }
        } catch (err) {
          showToast({ message: "Failed to process files", variant: "error" });
        }
      }
      window.addEventListener("message", handleNativeMessage);
      return () => window.removeEventListener("message", handleNativeMessage);
    }, [upload, showToast, memberId]);

    // Notify parent of file changes
    React.useEffect(() => {
      if (onFilesChange) onFilesChange(mediaFiles);
    }, [mediaFiles, onFilesChange]);

    // Expose handleDone, mediaFiles, uploading via ref
    useImperativeHandle(ref, () => ({
      handleDone,
      mediaFiles,
      uploading,
    }));

    async function handleDone() {
      if (!mediaFiles.length) {
        showToast({ message: "No files to add", variant: "error" });
        return;
      }
      let successCount = 0;
      let failCount = 0;
      for (const file of mediaFiles) {
        const doc: CreateDocumentDto = {
          media: {
            fileName: file.fileName,
            mimeType: file.mimeType,
            size: String(file.size),
            fileKey: file.fileKey,
            url: file.url,
            thumbnail: file.thumbnail,
          },
          category: FileCategory.Prescription, // Always Prescription for now
          status: DocumentStatus.Processed,
        };
        try {
          await createDocument(doc);
          successCount++;
        } catch (e) {
          failCount++;
        }
      }
      if (successCount)
        showToast({
          message: `${successCount} document(s) added`,
          variant: "success",
        });
      if (failCount)
        showToast({ message: `${failCount} failed`, variant: "error" });
      setMediaFiles([]);
    }

    return (
      <div className="flex flex-col gap-4 items-center">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
        <MButton
          type="button"
          variant="filled"
          leftIcon={<IconUpload size={20} />}
          onClick={() => {
            if (
              typeof window !== "undefined" &&
              !(window as any).ReactNativeWebView
            ) {
              fileInputRef.current?.click();
            } else {
              triggerNativeCameraCapture();
            }
          }}
          disabled={uploading}
        >
          {uploading ? "Uploading..." : "Upload Document"}
        </MButton>
        <div className="w-full flex flex-col gap-2">
          {mediaFiles.length === 0 && (
            <div className="text-neutral-400 text-center text-sm">
              No documents uploaded yet.
            </div>
          )}
          {mediaFiles.map((file) => (
            <div
              key={file.fileKey}
              className="flex items-center justify-between bg-gray-50 rounded px-3 py-2"
            >
              <span className="flex items-center gap-2">
                <IconFile size={18} />
                {file.fileName}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(file.fileKey)}
                aria-label="Delete file"
                className="hover:text-red-600 transition-colors"
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <IconTrash size={18} />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }
);
